// Ref: BE CODE-007 (case-logistics-polymorphic-schema)
import {
  RouteServiceDomainContractsEnumsLegType as ELegType,
  RouteServiceDomainContractsEnumsLegLocationType as ELocationType,
  RouteServiceDomainContractsEnumsLogisticProviderType as ELogisticProviderType,
  RouteServiceDomainContractsEnumsLegResourceType as EResourceType,
  RouteServiceDomainContractsEnumsRouteType as ERouteType,
  RouteServiceDomainContractsEnumsSegmentType as ESegmentType,
  RouteServiceDomainContractsEnumsTransportationType as ETransportationType,
  type schemas,
} from '#apiSchemas';
import {
  ECommercialLegType,
  EGroundVehicleType,
  ETransportProviderType,
} from '#shared';

import type { Nullishify } from '#shared';

export {
  RouteServiceDomainContractsEnumsLegType as ELegType,
  RouteServiceDomainContractsEnumsLegLocationType as ELocationType,
  RouteServiceDomainContractsEnumsLogisticProviderType as ELogisticProviderType,
  RouteServiceDomainContractsEnumsLegResourceType as EResourceType,
  RouteServiceDomainContractsEnumsRouteType as ERouteType,
  RouteServiceDomainContractsEnumsSegmentType as ESegmentType,
  RouteServiceDomainContractsEnumsTransportationType as ETransportationType,
} from '#apiSchemas';

export {
  ECommercialLegType,
  ETransportProviderType,
  EGroundVehicleType as EVehicleType,
} from '#shared';

// Discriminator enum for ILocationResponse — maps to C# [JsonDerivedType] discriminator strings
export enum ELocationResponseType {
  Airport = 'AirportLocationResponse',
  CustomAddress = 'CustomAddressResponse',
  Hospital = 'HospitalLocationResponse',
  Home = 'HomeLocationResponse',
  Hub = 'HubLocationResponse',
  TxCenter = 'TxCenterLocationResponse',
}

// Discriminator enum for IResourceResponse
export enum EResourceResponseType {
  Aircraft = 'AircraftResourceResponse',
  Crew = 'CrewResourceResponse',
  Employee = 'EmployeeResourceResponse',
  OcsConsole = 'OcsConsoleResourceResponse',
}

// Discriminator enum for IRouteLegResponse — canonical source for schema validation.
// EResponseRouteLegTypeEnum in nop.ts is now in sync (extended with TmdxDrive* values).
// ERouteLegResponseType is NOT merged into nop.ts because nop.caseLogistics.ts ↔ nop.ts
// would form a circular dependency. Both enums carry identical string values; this file
// remains the authority inside caseLogistics.schema.ts validation.
export enum ERouteLegResponseType {
  AccommodationLeg = 'AccommodationRouteLegResponse',
  CommercialFlightAviaLeg = 'CommercialFlightAviaRouteLegResponse',
  CurriServiceGroundLeg = 'CurriServiceGroundLegResponse',
  SupplementalLiftAviaLeg = 'SupplementalLiftAviaLegResponse',
  TmdxAffiliateGroundLeg = 'TmdxAffiliateGroundLegResponse',
  TmdxDriveAffiliateGroundLeg = 'TmdxDriveAffiliateGroundLegResponse',
  TmdxDriveTedsGroundLeg = 'TmdxDriveTedsGroundLegResponse',
  TmdxJetAviaLeg = 'TmdxJetAviaLegResponse',
  TxCenterProviderAviaLeg = 'TxCenterProviderAviaLegResponse',
  TxCenterProviderGroundLeg = 'TxCenterProviderGroundLegResponse',
  SelfServicedGroundLeg = 'SelfServicedGroundLegResponse',
}

// ─── Value objects ────────────────────────────────────────────────────────────

// LocationResponse aliased directly from nopSchema (auto-synced via Swagger)
export type CaseLogisticsLocationValueObject = Nullishify<
  schemas['RouteService.ApiContracts.Responses.CaseLogistics.ValueObjects.LocationResponse']
>;

// LocationReferenceResponse aliased directly from nopSchema
export type LocationReferenceResponse =
  schemas['RouteService.ApiContracts.Responses.CaseLogistics.ValueObjects.LocationReferenceResponse'];

// FboReferenceResponse — not in nopSchema, hand-authored from C# source
export type FboReferenceResponse = {
  fboLocationId: string;
  fboAddressId: number;
};

// FboLocationAddressResponse — not in nopSchema, hand-authored from C# source
export type FboLocationAddressResponse = Nullishify<{
  id: number;
  isPrimary: boolean;
  type: string;
  city?: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
}>;

// FboLocationResponse — not in nopSchema, hand-authored from C# source
export type FboLocationResponse = {
  id: string;
  maoId: number;
  name: string;
  organizationName: string;
  addresses: FboLocationAddressResponse[];
  createdAt: string;
};

export type AirportReferenceResponse = Nullishify<{
  locationId: string;
  airportId: string;
  fboReference: FboReferenceResponse | null;
  locationType: ELocationType.Airport;
}>;

export type GroundLocationReferenceResponse = Nullishify<{
  locationId: string;
  airportReference: AirportReferenceResponse | null;
  locationReference: LocationReferenceResponse | null;
  locationType: ELocationType;
}>;

export type ChainPositionResponse = Nullishify<{
  previousLegId: string | null;
  nextLegId: string | null;
  groupId: string;
}>;

export type CrewMemberResponse = Nullishify<{
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
}>;

export type LogisticsProviderReferenceResponse = {
  providerId: string;
  providerType: ELogisticProviderType;
};

export type ResourceReferenceResponse = {
  resourceId: string;
  resourceType: EResourceType;
};

// ─── Polymorphic locations (ILocationResponse) ───────────────────────────────

type BaseLocationResponse = {
  id: string;
  location: CaseLogisticsLocationValueObject;
  locationType: ELocationType;
  createdAt: string;
};

export type AirportLocationResponse = Nullishify<
  BaseLocationResponse & {
    $type: ELocationResponseType.Airport;
    locationType: ELocationType.Airport;
    maoId: number;
    name: string;
    icao: string | null;
    iata: string | null;
    faa: string | null;
    isSabreSupported: boolean;
    fboLocations: FboLocationResponse[];
  }
>;

export type CustomAddressResponse = BaseLocationResponse & {
  $type: ELocationResponseType.CustomAddress;
  locationType: ELocationType.Custom;
  hereMapsId: string;
};

export type HospitalLocationResponse = Nullishify<
  BaseLocationResponse & {
    $type: ELocationResponseType.Hospital;
    locationType: ELocationType.Hospital;
    name: string;
    code: string | null;
    logoPath: string | null;
  }
>;

export type HomeLocationResponse = BaseLocationResponse & {
  $type: ELocationResponseType.Home;
  locationType: ELocationType.HomeResidence;
};

export type HubLocationResponse = BaseLocationResponse & {
  $type: ELocationResponseType.Hub;
  locationType: ELocationType.Hub;
  name: string;
  code: string;
};

export type TxCenterLocationResponse = Nullishify<
  BaseLocationResponse & {
    $type: ELocationResponseType.TxCenter;
    locationType: ELocationType.TxCenter;
    name: string;
    code: string;
    logoPath: string | null;
  }
>;

export type CaseLogisticsLocation =
  | AirportLocationResponse
  | CustomAddressResponse
  | HospitalLocationResponse
  | HomeLocationResponse
  | HubLocationResponse
  | TxCenterLocationResponse;

// ─── Polymorphic resources (IResourceResponse) ───────────────────────────────

export type AircraftResourceResponse = Nullishify<{
  $type: EResourceResponseType.Aircraft;
  resourceId: string;
  resourceType: EResourceType.Aircraft;
  serialNumber: string | null;
  tailNumber: string;
  createdAt: string;
}>;

export type CrewResourceResponse = Nullishify<{
  $type: EResourceResponseType.Crew;
  resourceId: string;
  resourceType: EResourceType.Crew;
  maoId: number;
  code: string | null;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  createdAt: string;
}>;

export type EmployeeResourceResponse = Nullishify<{
  $type: EResourceResponseType.Employee;
  resourceId: string;
  // resourceType is settable for employees: ClinicalSpecialist or Surgeon
  resourceType: EResourceType.ClinicalSpecialist | EResourceType.Surgeon;
  userId: string | null;
  firstName: string;
  lastName: string;
  phoneNumber: string | null;
  email: string | null;
  photoPath: string | null;
  createdAt: string;
}>;

export type OcsConsoleResourceResponse = Nullishify<{
  $type: EResourceResponseType.OcsConsole;
  resourceId: string;
  resourceType: EResourceType.OCSConsole;
  name: string | null;
  serialNumber: string | null;
  fullSerialNumber: string | null;
  logoPath: string | null;
  createdAt: string;
}>;

export type CaseLogisticsResource =
  | AircraftResourceResponse
  | CrewResourceResponse
  | EmployeeResourceResponse
  | OcsConsoleResourceResponse;

/** Aircraft resources keyed by resourceId — build from CaseLogisticsResponse.resources filtered to Aircraft. */
export type LogisticsAircraftById = Map<string, AircraftResourceResponse>;

// ─── Route leg base shapes ────────────────────────────────────────────────────

type BaseRouteLegResponse = {
  id: string;
  segmentType: ESegmentType;
  resources: ResourceReferenceResponse[];
  createdAt: string;
  modifiedAt: string;
};

type BaseAviaLegResponse = Nullishify<
  BaseRouteLegResponse & {
    chainPosition: ChainPositionResponse;
    legType: ELegType.Air;
    departureLocation: AirportReferenceResponse;
    arrivalLocation: AirportReferenceResponse;
    departureTime: string;
    arrivalTime: string;
    departureLocalTimezone: string;
    arrivalLocalTimezone: string;
    comments: string | null;
    hasScs: boolean | null;
  }
>;

type BaseGroundLegResponse = Nullishify<
  BaseRouteLegResponse & {
    chainPosition: ChainPositionResponse;
    legType: ELegType.Ground;
    departureLocation: GroundLocationReferenceResponse;
    arrivalLocation: GroundLocationReferenceResponse;
    departureTime: string;
    arrivalTime: string;
    departureLocalTimezone: string;
    arrivalLocalTimezone: string;
    distanceInMeters: number;
    comments: string | null;
    hasScs: boolean | null;
  }
>;

// ─── Polymorphic legs (IRouteLegResponse) ────────────────────────────────────

export type TmdxJetAviaLegResponse = BaseAviaLegResponse & {
  $type: ERouteLegResponseType.TmdxJetAviaLeg;
  transportProviderType: ETransportProviderType.Tmdx;
};

export type SupplementalLiftAviaLegResponse = Nullishify<
  BaseAviaLegResponse & {
    $type: ERouteLegResponseType.SupplementalLiftAviaLeg;
    transportProviderType: ETransportProviderType.SupplementalLiftProvider;
    provider: LogisticsProviderReferenceResponse;
    firstCrewMember: CrewMemberResponse;
    secondCrewMember: CrewMemberResponse;
    tailNumber: string | null;
    priceInCents: number;
  }
>;

export type TxCenterProviderAviaLegResponse = Nullishify<
  BaseAviaLegResponse & {
    $type: ERouteLegResponseType.TxCenterProviderAviaLeg;
    transportProviderType: ETransportProviderType.TxCenter;
    provider: LogisticsProviderReferenceResponse;
    firstCrewMember: CrewMemberResponse;
    secondCrewMember: CrewMemberResponse;
    tailNumber: string | null;
  }
>;

export type CommercialFlightAviaLegResponse = Nullishify<
  BaseAviaLegResponse & {
    $type: ERouteLegResponseType.CommercialFlightAviaLeg;
    transportProviderType: ETransportProviderType.Commercial;
    sabreGroupId: string | null;
    flightNumber: string;
    bookingNumber: string | null;
    priceInCents: number | null;
    flightOperator: string | null;
    seatType: string | null;
    numberOfSeats: number | null;
    commercialLegType: ECommercialLegType;
  }
>;

export type TmdxAffiliateGroundLegResponse = Nullishify<
  BaseGroundLegResponse & {
    $type: ERouteLegResponseType.TmdxAffiliateGroundLeg;
    transportProviderType: ETransportProviderType.TMDXAffiliate;
    driver: string | null;
    email: string | null;
    phoneNumber: string | null;
    plateNumber: string | null;
    vehicleType: EGroundVehicleType | null;
    reservationNumber: string | null;
    pickup: string | null;
    dropoff: string | null;
    wait: string | null;
    stay: string | null;
    isFeasibleForDrive: boolean;
  }
>;

export type TxCenterProviderGroundLegResponse = Nullishify<
  BaseGroundLegResponse & {
    $type: ERouteLegResponseType.TxCenterProviderGroundLeg;
    transportProviderType: ETransportProviderType.TxCenter;
    provider: LogisticsProviderReferenceResponse;
    driver: string | null;
    email: string | null;
    phoneNumber: string | null;
    plateNumber: string | null;
    vehicleType: EGroundVehicleType | null;
    reservationNumber: string | null;
    pickup: string | null;
    dropoff: string | null;
    wait: string | null;
    stay: string | null;
    isFeasibleForDrive: boolean;
  }
>;

export type CurriServiceGroundLegResponse = Nullishify<
  BaseGroundLegResponse & {
    $type: ERouteLegResponseType.CurriServiceGroundLeg;
    transportProviderType: ETransportProviderType.Curri;
    trackingNumber: string | null;
  }
>;

export type TmdxDriveAffiliateGroundLegResponse = BaseGroundLegResponse & {
  $type: ERouteLegResponseType.TmdxDriveAffiliateGroundLeg;
  transportProviderType: ETransportProviderType.TMDXAffiliate;
  isFeasibleForDrive: boolean;
};

export type TmdxDriveTedsGroundLegResponse = BaseGroundLegResponse & {
  $type: ERouteLegResponseType.TmdxDriveTedsGroundLeg;
  transportProviderType: ETransportProviderType.Teds;
  isFeasibleForDrive: boolean;
};

// SpecRef: CODE-007
export type SelfServicedGroundLegResponse = BaseGroundLegResponse & {
  $type: ERouteLegResponseType.SelfServicedGroundLeg;
  transportProviderType: ETransportProviderType.SelfServiced;
  isFeasibleForDrive: boolean;
};

export type AccommodationRouteLegResponse = Nullishify<
  BaseRouteLegResponse & {
    $type: ERouteLegResponseType.AccommodationLeg;
    legType: ELegType.Accommodation;
    location: LocationReferenceResponse | null;
    checkInAt: string;
    checkOutAt: string;
    rooms: number | null;
    hotelName: string | null;
    bookingNumber: string | null;
    comments: string | null;
    hasScs: boolean | null;
  }
>;

export type CaseLogisticsRouteLeg =
  | TmdxJetAviaLegResponse
  | SupplementalLiftAviaLegResponse
  | TxCenterProviderAviaLegResponse
  | CommercialFlightAviaLegResponse
  | TmdxAffiliateGroundLegResponse
  | TxCenterProviderGroundLegResponse
  | CurriServiceGroundLegResponse
  | TmdxDriveAffiliateGroundLegResponse
  | TmdxDriveTedsGroundLegResponse
  | SelfServicedGroundLegResponse
  | AccommodationRouteLegResponse;

// ─── Route ────────────────────────────────────────────────────────────────────

export type LogisticsRouteResponse = Nullishify<{
  id: string;
  routeType: ERouteType;
  transportationType: ETransportationType;
  departureHub: LocationReferenceResponse | null;
  arrivalHub: LocationReferenceResponse | null;
  legs: CaseLogisticsRouteLeg[];
  createdAt: string;
  modifiedAt: string;
}>;

// ─── Root response ────────────────────────────────────────────────────────────

export type CaseLogisticsResponse = Omit<
  Nullishify<
    Omit<
      schemas['RouteService.ApiContracts.Responses.CaseLogistics.CaseLogisticsResponse'],
      'locations' | 'resources' | 'routes' | 'isTmdxDriveCompatible'
    >
  >,
  'logisticsProviders'
> & {
  logisticsProviders?:
    | {
        id: string;
        name?: string | null;
        providerType: ELogisticProviderType;
        createdAt: string;
      }[]
    | null;
  locations: CaseLogisticsLocation[];
  resources: CaseLogisticsResource[];
  routes: LogisticsRouteResponse[];
  isTmdxDriveCompatible?: boolean | null;
};

// Backward-compat re-exports for nop.ts consumers
export { ERouteLegResponseType as EResponseRouteLegTypeEnum };
export type {
  CaseLogisticsResponse as NOPCaseLogistics,
  CaseLogisticsRouteLeg as RouteLegResponseType,
};
